import type { AuthError } from '@supabase/supabase-js';
import { getSupabase } from '../backend/supabaseAdapter';
import { V2_AUTH_STORAGE_KEY, V2_KNOWN_ACCOUNT_KEY, V2_KNOWN_SESSION_KEY } from '../persistence/persistenceContract';
import { createStore } from './createStore';

// Auth session state — sign-in is REQUIRED (Owner task book 2026-09-04 §4): there is no guest
// mode, no "use locally first", no "sign in later". The Supabase *service* is reused by contract;
// session persistence lives under yanwushi-v2-auth (never the legacy sb-* default key), and
// nothing here reads the legacy app's stored session.
//
// Offline continuation: an inspector whose session was valid the last time the device was online
// keeps working offline. The email of that session is remembered under the yanwushi-v2 namespace
// (V2_KNOWN_SESSION_KEY) and cleared on sign-out; when the SDK cannot confirm the session because
// the device is offline, the app continues on the remembered one and says so (offlineContinuation).
//
// Account isolation (Owner device test 2026-09-21): the session names the ACCOUNT (its user id), and
// the device's data follows it — entering an account opens that account's own database and hydrates
// it BEFORE the phase turns signedIn; leaving closes it before the phase turns signedOut. The store
// reaches the data through hooks (registerAccountHooks, wired in src/main.tsx by
// src/state/accountLifecycle.ts) because every data store imports this one.
export type SessionPhase = 'unknown' | 'signedOut' | 'signedIn';

export interface SessionState {
  phase: SessionPhase;
  email: string | null;
  // The Supabase user id of the signed-in account; null when signed out (and in unit tests that seed
  // a session without one).
  userId: string | null;
  backendConfigured: boolean;
  // Running on the remembered session without a live confirmation (device offline).
  offlineContinuation: boolean;
}

export const sessionStore = createStore<SessionState>({
  phase: 'unknown',
  email: null,
  userId: null,
  backendConfigured: false,
  offlineContinuation: false
});

// What happens to the device's data when the account changes. `signingOut` and `signedOut` run on an
// EXPLICIT sign-out only:
//   signingOut  BEFORE the session ends — the account's last authenticated calls (its push registration
//               on this phone is removed while the server can still tell who is asking);
//   signedOut   after it — the identity provider's own session is ended there, so the next sign-in
//               offers the account chooser again.
export interface AccountHooks {
  enter(userId: string): Promise<void>;
  leave(): Promise<void>;
  signingOut?(): Promise<void>;
  signedOut?(): Promise<void>;
}

// How long an explicit sign-out waits for `signingOut`. Past it — or when the hook fails — the sign-out
// goes on: nothing that runs before the session ends may keep an inspector signed in.
export const SIGNING_OUT_LIMIT_MS = 5_000;

function withinLimit(work: () => Promise<void>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    Promise.resolve().then(work).then(done, done);
  });
}

let accountHooks: AccountHooks | null = null;

export function registerAccountHooks(hooks: AccountHooks | null): void {
  accountHooks = hooks;
}

// Every auth call returns a classified outcome; the UI translates the code (no copy lives here).
export type AuthCode =
  | 'ok'
  | 'checkEmail'
  | 'resetSent'
  | 'codeSent'
  | 'notConfigured'
  | 'invalidCredentials'
  | 'emailNotConfirmed'
  | 'emailTaken'
  | 'weakPassword'
  | 'invalidCode'
  | 'tooSoon'
  | 'network'
  | 'cancelled'
  | 'notNative'
  | 'appleUnavailable'
  | 'googleUnavailable'
  | 'unknown';

export interface AuthOutcome {
  ok: boolean;
  code: AuthCode;
  detail: string;
}

function readKnownSession(): string | null {
  try {
    const value = window.localStorage.getItem(V2_KNOWN_SESSION_KEY);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function rememberKnownSession(email: string | null, userId: string | null = null): void {
  try {
    if (email) window.localStorage.setItem(V2_KNOWN_SESSION_KEY, email);
    else window.localStorage.removeItem(V2_KNOWN_SESSION_KEY);
    if (email && userId) window.localStorage.setItem(V2_KNOWN_ACCOUNT_KEY, JSON.stringify({ userId, email }));
    else if (!email) window.localStorage.removeItem(V2_KNOWN_ACCOUNT_KEY);
  } catch {
    // storage refusal must never block field work
  }
}

function sameEmail(a: unknown, b: string): boolean {
  return typeof a === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase();
}

// Offline continuation knows the account by its remembered email; its DATABASE is named by the user
// id. The id comes from the note written beside the email, or — on the first launch after the update
// that introduced the note — from the session Supabase keeps under V2_AUTH_STORAGE_KEY (a read of this
// app's own storage key; the session itself is never trusted from it, only the id of the account the
// remembered email already names).
function knownUserIdFor(email: string): string | null {
  try {
    const note = JSON.parse(window.localStorage.getItem(V2_KNOWN_ACCOUNT_KEY) ?? 'null') as { userId?: unknown; email?: unknown } | null;
    if (note && typeof note.userId === 'string' && note.userId.length > 0 && sameEmail(note.email, email)) return note.userId;
  } catch {
    // fall through to the stored session
  }
  try {
    const stored = JSON.parse(window.localStorage.getItem(V2_AUTH_STORAGE_KEY) ?? 'null') as { user?: { id?: unknown; email?: unknown } } | null;
    const user = stored?.user;
    if (user && typeof user.id === 'string' && user.id.length > 0 && sameEmail(user.email, email)) return user.id;
  } catch {
    // no usable stored session
  }
  return null;
}

function online(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

export function classifyAuthError(error: AuthError | Error | null | undefined): AuthOutcome {
  if (!error) return { ok: true, code: 'ok', detail: '' };
  const text = error.message ?? '';
  if (/invalid login credentials|invalid_credentials/i.test(text)) return { ok: false, code: 'invalidCredentials', detail: text };
  if (/email not confirmed/i.test(text)) return { ok: false, code: 'emailNotConfirmed', detail: text };
  if (/already registered|already exists|user_already_exists/i.test(text)) return { ok: false, code: 'emailTaken', detail: text };
  if (/password should be|weak password|weak_password/i.test(text)) return { ok: false, code: 'weakPassword', detail: text };
  if (/token has expired|invalid token|otp_expired|invalid otp|token is invalid/i.test(text)) return { ok: false, code: 'invalidCode', detail: text };
  if (/only request this after|rate limit|too many requests|over_email_send_rate_limit/i.test(text)) return { ok: false, code: 'tooSoon', detail: text };
  if (/failed to fetch|network|load failed|fetch/i.test(text)) return { ok: false, code: 'network', detail: text };
  return { ok: false, code: 'unknown', detail: text };
}

interface ResolvedSession {
  phase: 'signedIn' | 'signedOut';
  email: string | null;
  userId: string | null;
  backendConfigured: boolean;
  offlineContinuation: boolean;
}

const SIGNED_OUT: ResolvedSession = { phase: 'signedOut', email: null, userId: null, backendConfigured: true, offlineContinuation: false };

// The remembered account, continued without a live confirmation. With the data hooks installed (the
// app) an account that cannot be NAMED by its user id is not continued — there would be no database to
// open for it, and guessing one is how one inspector's data reaches another.
function offlineContinuationOf(known: string): ResolvedSession {
  const userId = knownUserIdFor(known);
  if (userId === null && accountHooks !== null) return SIGNED_OUT;
  return { phase: 'signedIn', email: known, userId, backendConfigured: true, offlineContinuation: true };
}

async function resolveSession(): Promise<ResolvedSession> {
  const client = getSupabase();
  if (!client) return { ...SIGNED_OUT, backendConfigured: false };
  const known = readKnownSession();
  try {
    const { data, error } = await client.auth.getSession();
    const user = data.session?.user ?? null;
    const email = user?.email ?? null;
    if (user !== null && email !== null) {
      const userId = typeof user.id === 'string' && user.id.length > 0 ? user.id : null;
      // A session that does not name its account cannot have its data opened (see offlineContinuationOf).
      if (userId === null && accountHooks !== null) return SIGNED_OUT;
      rememberKnownSession(email, userId);
      return { phase: 'signedIn', email, userId, backendConfigured: true, offlineContinuation: false };
    }
    // No confirmed session. Offline (or a transport error) with a remembered session = keep working.
    if (known !== null && (!online() || error)) return offlineContinuationOf(known);
    return SIGNED_OUT;
  } catch {
    return known !== null ? offlineContinuationOf(known) : SIGNED_OUT;
  }
}

// Resolves who is signed in, moves the device's data to that account (or closes it), and only then
// publishes the phase: a screen that renders "signed in" always renders over that account's stores.
export async function initSession(): Promise<void> {
  const resolved = await resolveSession();
  if (accountHooks) {
    if (resolved.phase === 'signedIn' && resolved.userId !== null) await accountHooks.enter(resolved.userId);
    else await accountHooks.leave();
  }
  sessionStore.set((prev) => ({ ...prev, ...resolved }));
}

function notConfigured(): AuthOutcome {
  return { ok: false, code: 'notConfigured', detail: '' };
}

export async function signInWithPassword(email: string, password: string): Promise<AuthOutcome> {
  const client = getSupabase();
  if (!client) return notConfigured();
  const { error } = await client.auth.signInWithPassword({ email: email.trim(), password });
  return classifyAuthError(error);
}

// Create Account → the confirmation email goes out; the account is usable once the email is
// verified (the code from the email is entered on the verification step).
export async function createAccount(email: string, password: string): Promise<AuthOutcome> {
  const client = getSupabase();
  if (!client) return notConfigured();
  const { data, error } = await client.auth.signUp({ email: email.trim(), password });
  if (error) return classifyAuthError(error);
  // Supabase answers an existing address with a user that has no identities (no disclosure).
  if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) return { ok: false, code: 'emailTaken', detail: '' };
  if (data.session) return { ok: true, code: 'ok', detail: '' };
  return { ok: true, code: 'checkEmail', detail: '' };
}

export async function verifyEmailCode(email: string, code: string): Promise<AuthOutcome> {
  const client = getSupabase();
  if (!client) return notConfigured();
  const { error } = await client.auth.verifyOtp({ email: email.trim(), token: code.trim(), type: 'signup' });
  if (error) {
    // Some projects issue the confirmation code under the generic email type.
    const retry = await client.auth.verifyOtp({ email: email.trim(), token: code.trim(), type: 'email' });
    return classifyAuthError(retry.error);
  }
  return { ok: true, code: 'ok', detail: '' };
}

export async function resendVerificationEmail(email: string): Promise<AuthOutcome> {
  const client = getSupabase();
  if (!client) return notConfigured();
  const { error } = await client.auth.resend({ type: 'signup', email: email.trim() });
  return error ? classifyAuthError(error) : { ok: true, code: 'checkEmail', detail: '' };
}

// Sign in with a code sent to the account's e-mail (web version, 2026-10-20): the way into the web version for an
// inspector whose account was made with Apple or Google in the app and so has no password. It never creates an
// account (shouldCreateUser: false — an unknown address simply gets no code), and because the session is an
// e-mail-code session it also proves a new device (account-v2 accepts it as the device code, item 50).
export async function sendSignInCode(email: string): Promise<AuthOutcome> {
  const client = getSupabase();
  if (!client) return notConfigured();
  const { error } = await client.auth.signInWithOtp({ email: email.trim(), options: { shouldCreateUser: false } });
  // "Signups not allowed for otp" = no such account. Said the same way as a sent code (no disclosure).
  if (error && /signups? not allowed|user not found/i.test(error.message ?? '')) return { ok: true, code: 'codeSent', detail: '' };
  return error ? classifyAuthError(error) : { ok: true, code: 'codeSent', detail: '' };
}

export async function verifySignInCode(email: string, code: string): Promise<AuthOutcome> {
  const client = getSupabase();
  if (!client) return notConfigured();
  const { error } = await client.auth.verifyOtp({ email: email.trim(), token: code.trim(), type: 'email' });
  return classifyAuthError(error);
}

// Forgot / Reset Password: the reset email carries a code; the inspector enters it with the new
// password (verifyOtp type recovery → updateUser).
export async function sendPasswordReset(email: string): Promise<AuthOutcome> {
  const client = getSupabase();
  if (!client) return notConfigured();
  const { error } = await client.auth.resetPasswordForEmail(email.trim());
  return error ? classifyAuthError(error) : { ok: true, code: 'resetSent', detail: '' };
}

export async function completePasswordReset(email: string, code: string, newPassword: string): Promise<AuthOutcome> {
  const client = getSupabase();
  if (!client) return notConfigured();
  const verify = await client.auth.verifyOtp({ email: email.trim(), token: code.trim(), type: 'recovery' });
  if (verify.error) return classifyAuthError(verify.error);
  const update = await client.auth.updateUser({ password: newPassword });
  return classifyAuthError(update.error);
}

export async function completeSessionRefresh(): Promise<void> {
  await initSession();
}

export async function signOutAction(): Promise<void> {
  const client = getSupabase();
  // While the session is still valid: best effort and bounded, it never decides whether the sign-out happens.
  const signingOut = accountHooks?.signingOut;
  if (signingOut) await withinLimit(() => signingOut(), SIGNING_OUT_LIMIT_MS);
  if (client) {
    try {
      await client.auth.signOut();
    } catch {
      // the local session is cleared regardless; a failed server call never traps the inspector
    }
  }
  rememberKnownSession(null);
  if (accountHooks) {
    // The identity provider's own session first (best effort — it only decides whether the next
    // sign-in offers the account chooser), then the device's data: closed before the phase changes.
    await accountHooks.signedOut?.().catch(() => undefined);
    await accountHooks.leave();
  }
  sessionStore.set((prev) => ({ ...prev, email: null, userId: null, phase: 'signedOut', offlineContinuation: false }));
}

// Test / development seam: seeds a session state without a backend. Dead in production paths.
export function setSessionForTests(state: Partial<SessionState>): void {
  sessionStore.set((prev) => ({ ...prev, ...state }));
  if (state.phase === 'signedIn' && state.email) rememberKnownSession(state.email, state.userId ?? null);
  if (state.phase === 'signedOut') rememberKnownSession(null);
}

export function knownSessionEmail(): string | null {
  return readKnownSession();
}
